import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import {
  FiCheck,
  FiChevronDown,
  FiGrid,
  FiList,
  FiSearch,
  FiX,
} from "react-icons/fi";
import * as P from "./Products.styled";

const ProductCommandBar = ({
  search,
  onSearchChange,
  filters,
  onToggleFilter,
  sort,
  onSortChange,
  viewMode,
  onViewModeChange,
  resultCount,
  loading,
  onResetAll,
}) => {
  const { t } = useTranslation();
  const [sortOpen, setSortOpen] = useState(false);
  const sortRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    if (!sortOpen) return undefined;
    const handler = (event) => {
      if (sortRef.current && !sortRef.current.contains(event.target)) {
        setSortOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [sortOpen]);

  useEffect(() => {
    const handleKey = (event) => {
      const key = event.key ? event.key.toLowerCase() : "";
      if (key === "/" && document.activeElement !== searchRef.current) {
        const tag = (document.activeElement?.tagName || "").toLowerCase();
        if (tag === "input" || tag === "textarea") return;
        event.preventDefault();
        searchRef.current?.focus();
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, []);

  const sortOptions = useMemo(
    () => [
      {
        value: "recommended",
        label: t("products.sort_recommended", "Recommended"),
      },
      {
        value: "price-asc",
        label: t("products.sort_price_low", "Price: Low to High"),
      },
      {
        value: "price-desc",
        label: t("products.sort_price_high", "Price: High to Low"),
      },
      { value: "name-asc", label: t("products.sort_name", "Name: A to Z") },
    ],
    [t]
  );

  const currentSortLabel =
    sortOptions.find((option) => option.value === sort)?.label ??
    sortOptions[0].label;

  const activeFilterCount =
    (filters.organic ? 1 : 0) + (filters.pesticideFree ? 1 : 0);

  return (
    <P.CommandBar>
      <P.CommandTopRow>
        <P.SearchField>
          <FiSearch className="leading" size={18} aria-hidden="true" />
          <P.SearchInput
            ref={searchRef}
            type="search"
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder={t(
              "products.search_placeholder",
              "Search products…"
            )}
            aria-label={t(
              "products.search_placeholder",
              "Search products"
            )}
          />
          {search ? (
            <P.IconBtn
              type="button"
              onClick={() => onSearchChange("")}
              aria-label={t("products.search_clear", "Clear search")}
            >
              <FiX size={14} aria-hidden="true" />
            </P.IconBtn>
          ) : (
            <P.KbdHint aria-hidden="true">/</P.KbdHint>
          )}
        </P.SearchField>

        <P.SortMenu ref={sortRef}>
          <P.SortButton
            type="button"
            $open={sortOpen}
            onClick={() => setSortOpen((prev) => !prev)}
            aria-haspopup="listbox"
            aria-expanded={sortOpen}
          >
            <span>
              {t("products.sort_label", "Sort")}:{" "}
              <strong>{currentSortLabel}</strong>
            </span>
            <FiChevronDown aria-hidden="true" />
          </P.SortButton>
          <AnimatePresence>
            {sortOpen ? (
              <P.SortPopover
                as={motion.div}
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.15 }}
                role="listbox"
              >
                {sortOptions.map((option) => (
                  <P.SortItem
                    key={option.value}
                    type="button"
                    $active={option.value === sort}
                    role="option"
                    aria-selected={option.value === sort}
                    onClick={() => {
                      onSortChange(option.value);
                      setSortOpen(false);
                    }}
                  >
                    {option.label}
                    {option.value === sort ? (
                      <FiCheck aria-hidden="true" />
                    ) : null}
                  </P.SortItem>
                ))}
              </P.SortPopover>
            ) : null}
          </AnimatePresence>
        </P.SortMenu>

        <P.ViewToggle
          role="radiogroup"
          aria-label={t("products.view_toggle", "View")}
        >
          <P.ViewToggleBtn
            type="button"
            $active={viewMode === "grid"}
            role="radio"
            aria-checked={viewMode === "grid"}
            onClick={() => onViewModeChange("grid")}
          >
            <FiGrid aria-hidden="true" size={14} />
            {t("products.grid_view", "Grid")}
          </P.ViewToggleBtn>
          <P.ViewToggleBtn
            type="button"
            $active={viewMode === "list"}
            role="radio"
            aria-checked={viewMode === "list"}
            onClick={() => onViewModeChange("list")}
          >
            <FiList aria-hidden="true" size={14} />
            {t("products.list_view", "List")}
          </P.ViewToggleBtn>
        </P.ViewToggle>
      </P.CommandTopRow>

      <P.ChipRow>
        <P.ChipRowLabel>
          {t("products.filters_label", "Filters")}
        </P.ChipRowLabel>
        <P.Chip
          type="button"
          $active={filters.organic}
          aria-pressed={filters.organic}
          onClick={() => onToggleFilter("organic")}
        >
          {filters.organic ? <FiCheck aria-hidden="true" size={12} /> : null}
          {t("products.filter_organic", "Organic")}
        </P.Chip>
        <P.Chip
          type="button"
          $active={filters.pesticideFree}
          aria-pressed={filters.pesticideFree}
          onClick={() => onToggleFilter("pesticideFree")}
        >
          {filters.pesticideFree ? (
            <FiCheck aria-hidden="true" size={12} />
          ) : null}
          {t("products.filter_pesticide_free", "Pesticide-Free")}
        </P.Chip>

        {!loading && resultCount != null ? (
          <P.ResultsCount style={{ marginLeft: "auto" }}>
            <strong>{resultCount}</strong>{" "}
            {t("products.results", "results")}
          </P.ResultsCount>
        ) : null}

        {activeFilterCount > 0 || search ? (
          <P.ResetLink type="button" onClick={onResetAll}>
            {t("products.clear_filters", "Clear all")}
          </P.ResetLink>
        ) : null}
      </P.ChipRow>
    </P.CommandBar>
  );
};

export default ProductCommandBar;
